/**
 * Mock data for the PermissionsAdmin module. Mirrors the matrix in
 * c-users-ahmad-downloads-copy-of-carbon-toasty-abelson.md so the seven
 * system presets light up the editor in a state that matches the spec.
 *
 * This file disappears the day the backend ships — every consumer goes
 * through permissionsApi.js, which routes around the mock when USE_MOCK
 * flips to false.
 */

import { ACTIONS, MATRIX_PAGES, ROLE_SLUGS } from 'modules/Common/config/permissionsMatrix';

const ALL = [ACTIONS.read, ACTIONS.create, ACTIONS.edit, ACTIONS.delete];
const READ = [ACTIONS.read];

const grantsToPermissions = (grantsByResource) => {
  const out = [];
  for (const [resource, actions] of Object.entries(grantsByResource)) {
    for (const action of actions) out.push(`org:${resource}:${action}`);
  }
  return out;
};

const baseReadOnlyMap = () => {
  const m = {};
  for (const page of MATRIX_PAGES) m[page.resourceKey] = READ.slice();
  return m;
};

const fullMap = (...keys) => Object.fromEntries(keys.map((k) => [k, ALL.slice()]));

const upgrade = (base, overrides) => ({ ...base, ...overrides });

// ----- per-role grant maps (resource → actions[]) -----

const adminGrants = Object.fromEntries(MATRIX_PAGES.map((p) => [p.resourceKey, ALL.slice()]));

const guestGrants = baseReadOnlyMap();

const tradeComplianceGrants = upgrade(baseReadOnlyMap(), {
  ...fullMap(
    'declarant_upload_hub',
    'declarant_download_hub',
    'imports',
    'suppliers',
    'installations',
    'emissions',
    'companies',
    'benchmarks',
    'declarant_reports',
    'operator_reports',
  ),
});

const financeGrants = upgrade(baseReadOnlyMap(), {
  ...fullMap(
    'declarant_certificate_price',
    'declarant_cost_radar',
    'cbam_costs',
    'cbam_quotes',
    'operator_certificate_price',
    'operator_cost_radar',
    'operator_premiums',
  ),
});

const sourcingGrants = upgrade(baseReadOnlyMap(), {
  ...fullMap(
    'suppliers',
    'installations',
    'emissions',
    'companies',
    'benchmarks',
    'connections',
    'connection_requests',
  ),
});

const productComplianceGrants = upgrade(baseReadOnlyMap(), {
  ...fullMap(
    'declarant_upload_hub',
    'declarant_download_hub',
    'imports',
    'suppliers',
    'installations',
    'emissions',
    'companies',
    'benchmarks',
    'declarant_reports',
    'operator_reports',
    'connections',
    'connection_requests',
  ),
});

const sustainabilityGrants = upgrade(baseReadOnlyMap(), {
  ...fullMap(
    'operator_home',
    'operator_upload_hub',
    'operator_download_hub',
    'inventory',
    'operator_suppliers',
    'operator_installations',
    'purchases',
    'sell_orders',
    'operator_reports',
    'operator_overview',
    'operator_certificate_price',
    'operator_cost_radar',
    'operator_premiums',
    'connections',
    'connection_requests',
    'verification',
  ),
});

const ROLE_PRESETS = [
  {
    slug: ROLE_SLUGS.Admin,
    name: 'Administrator',
    description: 'Full create/read/edit/delete on every resource. System role.',
    isSystem: true,
    grants: adminGrants,
  },
  {
    slug: ROLE_SLUGS.TradeCompliance,
    name: 'Trade Compliance SME',
    description:
      'Manages CBAM declarant data: imports, suppliers, installations, emissions, reports.',
    isSystem: true,
    grants: tradeComplianceGrants,
  },
  {
    slug: ROLE_SLUGS.Finance,
    name: 'Finance SME',
    description: 'Manages CBAM cost analysis and certificate pricing.',
    isSystem: true,
    grants: financeGrants,
  },
  {
    slug: ROLE_SLUGS.Sourcing,
    name: 'Sourcing SME',
    description: 'Manages supplier/installation data.',
    isSystem: true,
    grants: sourcingGrants,
  },
  {
    slug: ROLE_SLUGS.ProductCompliance,
    name: 'Product Compliance SME',
    description: 'Cross-functional manager of CBAM declarant + operator data.',
    isSystem: true,
    grants: productComplianceGrants,
  },
  {
    slug: ROLE_SLUGS.Sustainability,
    name: 'Sustainability SME',
    description: 'Manages operator (MFG) data: installations, emissions, premiums, verification.',
    isSystem: true,
    grants: sustainabilityGrants,
  },
  {
    slug: ROLE_SLUGS.Guest,
    name: 'Guest',
    description: 'Read-only access to every resource. System role.',
    isSystem: true,
    grants: guestGrants,
  },
];

// In-memory mutable store so create/edit/delete behave like a real backend.
const state = {
  roles: ROLE_PRESETS.map((r) => ({
    slug: r.slug,
    name: r.name,
    description: r.description,
    isSystem: r.isSystem,
    userCount: r.slug === ROLE_SLUGS.Admin ? 2 : r.slug === ROLE_SLUGS.Guest ? 5 : 1,
    updatedAt: '2026-06-22T14:30:00Z',
    updatedBy: 'system',
    grants: { ...r.grants },
  })),
};

const summarizeRole = (r) => ({
  slug: r.slug,
  name: r.name,
  description: r.description,
  isSystem: r.isSystem,
  userCount: r.userCount,
  updatedAt: r.updatedAt,
  updatedBy: r.updatedBy,
});

export const mock = {
  listRoles: async () => state.roles.map(summarizeRole),

  getRoleGrants: async (slug) => {
    const r = state.roles.find((x) => x.slug === slug);
    if (!r) throw new Error(`Role not found: ${slug}`);
    return { permissions: grantsToPermissions(r.grants) };
  },

  createRole: async ({ name, description }) => {
    // Generate a slug from the name; collisions get a numeric suffix.
    const base = `org:${name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')}`;
    const usedSlugs = new Set(state.roles.map((r) => r.slug));
    let slug = base;
    let i = 1;
    while (usedSlugs.has(slug)) slug = `${base}_${++i}`;
    const role = {
      slug,
      name,
      description: description || '',
      isSystem: false,
      userCount: 0,
      updatedAt: new Date().toISOString(),
      updatedBy: 'you',
      grants: baseReadOnlyMap(),
    };
    state.roles.push(role);
    return summarizeRole(role);
  },

  updateRole: async (slug, patch) => {
    const r = state.roles.find((x) => x.slug === slug);
    if (!r) throw new Error(`Role not found: ${slug}`);
    if (r.isSystem) throw new Error('Cannot edit a system role');
    if (patch.name !== undefined) r.name = patch.name;
    if (patch.description !== undefined) r.description = patch.description;
    r.updatedAt = new Date().toISOString();
    r.updatedBy = 'you';
    return summarizeRole(r);
  },

  deleteRole: async (slug) => {
    const r = state.roles.find((x) => x.slug === slug);
    if (!r) throw new Error(`Role not found: ${slug}`);
    if (r.isSystem) throw new Error('Cannot delete a system role');
    if (r.userCount > 0) throw new Error(`Cannot delete: ${r.userCount} user(s) still assigned`);
    state.roles = state.roles.filter((x) => x.slug !== slug);
  },

  updateRoleGrants: async (slug, permissions) => {
    const r = state.roles.find((x) => x.slug === slug);
    if (!r) throw new Error(`Role not found: ${slug}`);
    if (r.isSystem) throw new Error('Cannot edit a system role');
    const grants = {};
    for (const perm of permissions) {
      // perm shape: org:{resource}:{action}
      const match = /^org:([a-z_]+):(read|create|edit|delete)$/.exec(perm);
      if (!match) continue;
      const [, resource, action] = match;
      if (!grants[resource]) grants[resource] = [];
      if (!grants[resource].includes(action)) grants[resource].push(action);
    }
    r.grants = grants;
    r.updatedAt = new Date().toISOString();
    r.updatedBy = 'you';
  },
};
